import { format as dateFormat, format, isValid as isValidDate } from 'date-fns';

/**
 * Converts a timestamp to a string using a predefined format
 * @param {number} timestamp - Unix timestamp
 * @returns {string} Formatted date string
 */
export function timestampToString(timestamp) {
  if (!timestamp) return '';
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const date = new Date(parseInt(timestamp, 10) * 1000);
  let hours = date.getHours();
  const suffix = (hours >= 12) ? 'pm' : 'am';

  if (hours === 0) {
    hours = 12;
  } else if (hours > 12) {
    hours -= 12;
  }

  // 0-pad the minutes
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${monthNames[date.getMonth()]} ${date.getDate()} ${date.getFullYear()} - ${hours}:${minutes}${suffix}`;
}

/**
 * Formats a date string as MM/DD/YYYY
 * @param {string|Date} str - Date string or Date object
 * @returns {string} Formatted date string
 */
export function timestamp(str) {
  if (!str) return '';

  const date = new Date(str);
  return ('0' + (date.getMonth() + 1)).slice(-2) + '/' + ('0' + date.getDate()).slice(-2) + '/' + date.getFullYear();
}

/**
 * Converts milliseconds to MM:SS format
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} Formatted time string
 */
export function toMinutesAndSeconds(milliseconds) {
  const minutes = Math.floor(milliseconds / 60000);
  const seconds = ((milliseconds % 60000) / 1000).toFixed(0);

  return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
}

/**
 * Formats a timestamp as MM/DD/YYYY
 * @param {string|number} str - Timestamp string or number
 * @returns {string} Formatted date string
 */
export function formatTimestamp(str) {
  const d = new Date(+str);
  const currDate = d.getDate();
  const currMonth = d.getMonth() + 1;
  const currYear = d.getFullYear();

  return currMonth + '/' + currDate + '/' + currYear;
}

/**
 * Formats a date as MM/dd
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date string
 */
export function monthDay(date) {
  return format(new Date(date), 'MM/dd');
}

/**
 * Formats a game/event display name
 * @param {Object} game - Game object with scheduledFor, participantType, teams, etc.
 * @returns {string} Formatted event display name
 */
export function eventDisplayName(game) {
  const scheduledFor = game?.scheduledFor;
  const participantType = game?.participantType;
  const parsedScheduledFor = scheduledFor ? new Date(scheduledFor) : null;
  const eventDate = parsedScheduledFor && isValidDate(parsedScheduledFor)
    ? dateFormat(parsedScheduledFor, 'MM/dd/yyyy')
    : '';

  const awayTeamAlias = game?.awayTeam?.teamAlias;
  const homeTeamAlias = game?.homeTeam?.teamAlias;
  if (participantType === 'team_v_team' || (awayTeamAlias && homeTeamAlias)) {
    return eventDate
      ? `${eventDate}: ${awayTeamAlias || 'TBD'} @ ${homeTeamAlias || 'TBD'}`
      : `${awayTeamAlias || 'TBD'} @ ${homeTeamAlias || 'TBD'}`;
  }

  const eventTitle = game?.eventTitle || 'TBD';
  return eventDate ? `${eventDate} @ ${eventTitle}` : `@ ${eventTitle}`;
}

/**
 * Formats a player display name as "LastName, FirstName" or "LastName"
 * @param {Object} player - Player object with firstName and lastName
 * @returns {string} Formatted player name
 */
export function playerDisplayName(player) {
  let firstName = player?.firstName || '';
  let lastName = player?.lastName || '';
  if (!lastName.length && firstName.length) {
    lastName = firstName;
    firstName = '';
  }
  if (!lastName.length) {
    lastName = 'Unknown Player';
  }

  return (!firstName.length)
    ? lastName
    : `${lastName}, ${firstName}`;
}
